class TabbedPanels extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.setupKeyboardNavigation();
    this.setupActiveClass();
    this.observeRadioChanges();
    this.checkUrlParameter();
    this.setupPopstateListener();
    this.replaceGlobalBlockLabels();
  }

  disconnectedCallback() {
    // Clean up the popstate listener when component is removed
    window.removeEventListener('popstate', this.boundPopstateHandler);
  }

  setupKeyboardNavigation() {
    const tabLabels = this.querySelectorAll('.tabbed-panels__tab');
    this.tabs = Array.from(tabLabels);

    if (this.tabs.length === 0) return;

    // Add keyboard event listeners to each tab
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('keydown', (e) => this.handleKeyDown(e, index));
      tab.addEventListener('click', () => this.updateTabIndex(index));
    });

    // Find initially checked radio to set correct tabindex
    const checkedRadio = this.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      const checkedId = checkedRadio.id;
      const checkedIndex = this.tabs.findIndex(tab => tab.getAttribute('for') === checkedId);
      if (checkedIndex !== -1) {
        this.updateTabIndex(checkedIndex);
      }
    }
  }

  handleKeyDown(event, currentIndex) {
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
        break;

      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
        break;

      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;

      case 'End':
        event.preventDefault();
        newIndex = this.tabs.length - 1;
        break;

      default:
        return;
    }

    // Activate the new tab
    this.activateTab(newIndex);
  }

  activateTab(index, updateUrl = true) {
    const tab = this.tabs[index];
    if (!tab) return;

    // Remove active class from all tabs
    this.tabs.forEach(t => t.classList.remove('active'));
    
    // Add active class to the new tab
    tab.classList.add('active');

    // Click the label to check the radio button
    tab.click();
    
    // Focus the tab
    tab.focus();
    
    // Update tabindex
    this.updateTabIndex(index);
    
    // Update ARIA attributes
    this.updateAriaAttributes(index);
    
    // Update URL with query parameter
    if (updateUrl) {
      this.updateUrlParameter(index);
    }
  }

  updateTabIndex(activeIndex) {
    this.currentIndex = activeIndex;
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === activeIndex ? '0' : '-1');
      
      // Also update active class to stay in sync
      if (index === activeIndex) {
        tab.classList.add('active');
      } else {
        tab.classList.remove('active');
      }
    });
  }

  updateAriaAttributes(activeIndex) {
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
    });
  }

  setupActiveClass() {
    // Set initial active class based on checked radio
    const checkedRadio = this.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      const checkedId = checkedRadio.id;
      const activeTab = this.querySelector(`label[for="${checkedId}"]`);
      if (activeTab) {
        activeTab.classList.add('active');
      }
    }
  }

  observeRadioChanges() {
    // Listen for radio button changes
    const radios = this.querySelectorAll('input[type="radio"]');
    radios.forEach((radio, index) => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Remove active class from all tabs
          this.tabs.forEach(tab => tab.classList.remove('active'));
          
          // Add active class to the clicked tab
          const activeTab = this.querySelector(`label[for="${e.target.id}"]`);
          if (activeTab) {
            activeTab.classList.add('active');
            // Update URL when user clicks a tab
            this.updateUrlParameter(index);
          }
        }
      });
    });
  }

  checkUrlParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab_panel');
    
    if (tabParam !== null) {
      // Convert to 0-based index (URL uses 1-based: ?tab_panel=1 means first tab)
      const tabIndex = parseInt(tabParam, 10) - 1;
      
      if (tabIndex >= 0 && tabIndex < this.tabs.length) {
        // Activate the tab without updating URL (to avoid infinite loop)
        this.activateTab(tabIndex, false);
      }
    }
  }

  setupPopstateListener() {
    // Bind the handler so we can remove it later
    this.boundPopstateHandler = () => {
      this.checkUrlParameter();
    };
    
    // Listen for browser back/forward button
    window.addEventListener('popstate', this.boundPopstateHandler);
  }

  updateUrlParameter(index) {
    // Use 1-based index for URL (more user-friendly)
    const tabNumber = index + 1;
    const url = new URL(window.location);
    url.searchParams.set('tab_panel', tabNumber);
    
    // Update URL without page reload
    window.history.pushState({}, '', url);
  }

  replaceGlobalBlockLabels() {
    const tabPanelDatas = document.querySelectorAll('.tab-panel-data');
    
    if (tabPanelDatas.length === 0) return;
    const dataMap = {};
    tabPanelDatas.forEach(tabPanelData => {
      try {
        const data = JSON.parse(tabPanelData.textContent);
        dataMap[data.id] = data.tab_label;
      } catch (e) {
        console.warn('Invalid tab-panel-data JSON', e);
      }
    });
    this.querySelectorAll('.tab-label').forEach(label => {
      const id = label.dataset.tabId;
      const value = dataMap[id];

      if (value && value.trim() !== '') {
        label.textContent = value;
      }
    });
  }

}

export default TabbedPanels;

